import { useState, useEffect, useCallback, useRef } from "react";
import { Link } from "wouter";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  useListWebhookEndpoints,
  useCreateWebhookEndpoint,
  useUpdateWebhookEndpoint,
  useDeleteWebhookEndpoint,
  useRotateWebhookEndpointSecret,
  useSendWebhookTestEvent,
  getListWebhookEndpointsQueryKey,
  listWebhookDeliveries,
  WebhookEventType,
  type WebhookDeliveryStatus,
} from "@workspace/api-client-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Switch } from "@/components/ui/switch";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Loader2, Plus, Trash2, Copy, ExternalLink, RefreshCw, AlertTriangle, Zap, CheckCircle2, XCircle } from "lucide-react";

interface TestDeliveryResult {
  deliveryId: number;
  endpointId: number;
  status: WebhookDeliveryStatus | "polling" | "timeout";
  statusCode: number | null;
  error: string | null;
}

const POLL_INTERVAL_MS = 1000;
const MAX_POLL_ATTEMPTS = 30;
const AUTO_DISMISS_MS = 5000;
const MAX_RETRIES = 3;

function useTestDeliveryPoller() {
  const [results, setResults] = useState<Map<number, TestDeliveryResult>>(
    () => new Map(),
  );
  const abortControllersRef = useRef<Map<number, AbortController>>(new Map());
  const retryCountsRef = useRef<Map<number, number>>(new Map());

  const dismiss = useCallback((endpointId: number) => {
    setResults((prev) => {
      const next = new Map(prev);
      next.delete(endpointId);
      return next;
    });
    const ac = abortControllersRef.current.get(endpointId);
    if (ac) {
      ac.abort();
      abortControllersRef.current.delete(endpointId);
    }
  }, []);

  const resetRetries = useCallback((endpointId: number) => {
    retryCountsRef.current.delete(endpointId);
  }, []);

  const getRetryCount = useCallback((endpointId: number) => {
    return retryCountsRef.current.get(endpointId) ?? 0;
  }, []);

  const startPolling = useCallback(
    (endpointId: number, deliveryId: number, isRetry = false) => {
      if (isRetry) {
        const current = retryCountsRef.current.get(endpointId) ?? 0;
        if (current >= MAX_RETRIES) return;
        retryCountsRef.current.set(endpointId, current + 1);
      }

      const existing = abortControllersRef.current.get(endpointId);
      if (existing) existing.abort();

      const ac = new AbortController();
      abortControllersRef.current.set(endpointId, ac);

      setResults((prev) => {
        const next = new Map(prev);
        next.set(endpointId, {
          deliveryId,
          endpointId,
          status: "polling",
          statusCode: null,
          error: null,
        });
        return next;
      });

      (async () => {
        for (let attempt = 0; attempt < MAX_POLL_ATTEMPTS; attempt++) {
          if (ac.signal.aborted) return;

          await new Promise((r) => setTimeout(r, POLL_INTERVAL_MS));
          if (ac.signal.aborted) return;

          try {
            const data = await listWebhookDeliveries(endpointId);
            const delivery = data.deliveries.find((d) => d.id === deliveryId);
            if (!delivery || delivery.status === "pending") continue;

            if (ac.signal.aborted) return;
            abortControllersRef.current.delete(endpointId);

            setResults((prev) => {
              const next = new Map(prev);
              next.set(endpointId, {
                deliveryId,
                endpointId,
                status: delivery.status,
                statusCode: delivery.lastStatusCode,
                error: delivery.lastError,
              });
              return next;
            });

            setTimeout(() => dismiss(endpointId), AUTO_DISMISS_MS);
            return;
          } catch {
            // Silently retry on network errors
          }
        }

        if (ac.signal.aborted) return;
        abortControllersRef.current.delete(endpointId);

        setResults((prev) => {
          const next = new Map(prev);
          next.set(endpointId, {
            deliveryId,
            endpointId,
            status: "timeout",
            statusCode: null,
            error: "Timed out waiting for delivery result",
          });
          return next;
        });
      })();
    },
    [dismiss],
  );

  useEffect(() => {
    const controllers = abortControllersRef.current;
    return () => {
      for (const ac of controllers.values()) {
        ac.abort();
      }
    };
  }, []);

  return { results, startPolling, dismiss, resetRetries, getRetryCount };
}

const AUTO_PAUSE_THRESHOLD = 20;

const NON_SUBSCRIBABLE_EVENTS: string[] = ["webhook.test"];

const ALL_EVENT_TYPES: WebhookEventType[] = (
  Object.values(WebhookEventType) as WebhookEventType[]
).filter((t) => !NON_SUBSCRIBABLE_EVENTS.includes(t));

export default function WebhooksPage() {
  const qc = useQueryClient();
  const listQuery = useListWebhookEndpoints();
  const endpoints = listQuery.data?.endpoints ?? [];

  const [createOpen, setCreateOpen] = useState(false);
  const [revealedSecret, setRevealedSecret] = useState<string | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<number | null>(null);
  const [confirmRotateId, setConfirmRotateId] = useState<number | null>(null);

  const { results: testResults, startPolling, dismiss: dismissTestResult, resetRetries, getRetryCount } =
    useTestDeliveryPoller();

  const refetch = () =>
    qc.invalidateQueries({ queryKey: getListWebhookEndpointsQueryKey() });

  const updateMutation = useUpdateWebhookEndpoint({
    mutation: {
      onSuccess: () => {
        toast.success("Webhook updated");
        void refetch();
      },
      onError: (err: Error) => toast.error(err.message),
    },
  });

  const deleteMutation = useDeleteWebhookEndpoint({
    mutation: {
      onSuccess: () => {
        toast.success("Webhook deleted");
        setConfirmDeleteId(null);
        void refetch();
      },
      onError: (err: Error) => toast.error(err.message),
    },
  });

  const testMutation = useSendWebhookTestEvent({
    mutation: {
      onSuccess: (data, variables) => {
        toast.success("Test event sent");
        resetRetries(variables.id);
        startPolling(variables.id, data.deliveryId);
        void refetch();
      },
      onError: (err: Error) => toast.error(err.message),
    },
  });

  const rotateMutation = useRotateWebhookEndpointSecret({
    mutation: {
      onSuccess: (data) => {
        toast.success("New signing secret generated");
        setConfirmRotateId(null);
        setRevealedSecret(data.secret);
        void refetch();
      },
      onError: (err: Error) => toast.error(err.message),
    },
  });

  return (
    <div className="p-6 max-w-5xl mx-auto space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Webhooks</h1>
          <p className="text-sm text-muted-foreground">
            Receive signed event notifications when emails are delivered, opened,
            clicked, bounced, complained, or replied to.
          </p>
        </div>
        <Button onClick={() => setCreateOpen(true)}>
          <Plus className="mr-2 h-4 w-4" /> New endpoint
        </Button>
      </div>

      {listQuery.isLoading ? (
        <div className="flex items-center gap-2 text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading…
        </div>
      ) : endpoints.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center text-muted-foreground">
            No webhook endpoints yet. Click <strong>New endpoint</strong> to
            register one.
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {endpoints.map((ep) => (
            <Card key={ep.id}>
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between gap-4">
                  <div className="min-w-0 flex-1">
                    <CardTitle className="text-base font-mono break-all">
                      {ep.url}
                    </CardTitle>
                    <CardDescription className="mt-1 flex flex-wrap gap-1.5">
                      {ep.eventTypes.map((t) => (
                        <Badge key={t} variant="secondary" className="text-xs">
                          {t}
                        </Badge>
                      ))}
                    </CardDescription>
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    {ep.consecutiveFailures > 0 && !ep.pausedAt && (
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Badge
                              variant="outline"
                              className="border-amber-500 text-amber-600 bg-amber-50 dark:bg-amber-950/30 gap-1 cursor-default"
                              data-testid={`badge-failures-${ep.id}`}
                            >
                              <AlertTriangle className="h-3 w-3" />
                              {ep.consecutiveFailures}/{AUTO_PAUSE_THRESHOLD} failures
                            </Badge>
                          </TooltipTrigger>
                          <TooltipContent>
                            <p>{ep.consecutiveFailures} consecutive failures — auto-pauses at {AUTO_PAUSE_THRESHOLD}</p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    )}
                    <div className="flex items-center gap-2">
                      <Switch
                        checked={ep.active}
                        disabled={updateMutation.isPending}
                        onCheckedChange={(checked) => {
                          updateMutation.mutate({
                            id: ep.id,
                            data: { active: checked },
                          });
                        }}
                      />
                      <span className="text-xs text-muted-foreground">
                        {ep.active ? "Active" : "Paused"}
                      </span>
                    </div>
                    <Link href={`/app/webhooks/${ep.id}`}>
                      <Button variant="outline" size="sm">
                        Deliveries <ExternalLink className="ml-1.5 h-3 w-3" />
                      </Button>
                    </Link>
                    <Button
                      variant="ghost"
                      size="icon"
                      title="Send test event"
                      aria-label="Send test event"
                      disabled={testMutation.isPending}
                      onClick={() => testMutation.mutate({ id: ep.id })}
                    >
                      {testMutation.isPending &&
                      testMutation.variables?.id === ep.id ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Zap className="h-4 w-4" />
                      )}
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      title="Rotate signing secret"
                      aria-label="Rotate signing secret"
                      onClick={() => setConfirmRotateId(ep.id)}
                    >
                      <RefreshCw className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      title="Delete endpoint"
                      aria-label="Delete endpoint"
                      onClick={() => setConfirmDeleteId(ep.id)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="pt-0 space-y-2">
                <div className="text-xs text-muted-foreground font-mono">
                  Secret: {ep.secretPrefix}…
                </div>
                {testResults.has(ep.id) && (
                  <TestDeliveryStatusBadge
                    result={testResults.get(ep.id)!}
                    retryCount={getRetryCount(ep.id)}
                    maxRetries={MAX_RETRIES}
                    onDismiss={() => dismissTestResult(ep.id)}
                    onRetry={() => {
                      const r = testResults.get(ep.id)!;
                      startPolling(r.endpointId, r.deliveryId, true);
                    }}
                  />
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <CreateEndpointDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        onCreated={(secret) => {
          setRevealedSecret(secret);
          void refetch();
        }}
      />

      <RevealSecretDialog
        secret={revealedSecret}
        onClose={() => setRevealedSecret(null)}
      />

      <AlertDialog
        open={confirmDeleteId !== null}
        onOpenChange={(open) => !open && setConfirmDeleteId(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this webhook endpoint?</AlertDialogTitle>
            <AlertDialogDescription>
              No further events will be sent to this URL and the delivery
              history for it will also be removed. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (confirmDeleteId !== null) {
                  deleteMutation.mutate({ id: confirmDeleteId });
                }
              }}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog
        open={confirmRotateId !== null}
        onOpenChange={(open) => !open && setConfirmRotateId(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Rotate this webhook's signing secret?</AlertDialogTitle>
            <AlertDialogDescription>
              A fresh <code className="font-mono">whsec_…</code> secret will be
              generated and shown <strong>exactly once</strong>. The previous
              secret will stop verifying immediately, so update your
              subscriber's configuration as soon as you save the new value.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={rotateMutation.isPending}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              disabled={rotateMutation.isPending}
              onClick={(e) => {
                e.preventDefault();
                if (confirmRotateId !== null) {
                  rotateMutation.mutate({ id: confirmRotateId });
                }
              }}
            >
              {rotateMutation.isPending && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              Rotate secret
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function TestDeliveryStatusBadge({
  result,
  retryCount,
  maxRetries,
  onDismiss,
  onRetry,
}: {
  result: TestDeliveryResult;
  retryCount: number;
  maxRetries: number;
  onDismiss: () => void;
  onRetry: () => void;
}) {
  const retriesExhausted = retryCount >= maxRetries;

  if (result.status === "polling") {
    return (
      <div
        className="flex items-center gap-2 text-xs text-muted-foreground animate-in fade-in slide-in-from-top-1 duration-200"
        data-testid="test-delivery-polling"
      >
        <Loader2 className="h-3.5 w-3.5 animate-spin" />
        <span>Waiting for delivery result…</span>
      </div>
    );
  }

  if (result.status === "delivered") {
    return (
      <div
        className="flex items-center gap-2 text-xs animate-in fade-in slide-in-from-top-1 duration-200"
        data-testid="test-delivery-success"
      >
        <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" />
        <span className="text-emerald-700 dark:text-emerald-400 font-medium">
          Delivered
          {result.statusCode != null && (
            <> &middot; HTTP {result.statusCode}</>
          )}
        </span>
        <button
          onClick={onDismiss}
          className="ml-1 text-muted-foreground hover:text-foreground"
          aria-label="Dismiss"
        >
          ✕
        </button>
      </div>
    );
  }

  if (result.status === "timeout") {
    return (
      <div
        className="flex items-center gap-2 text-xs animate-in fade-in slide-in-from-top-1 duration-200"
        data-testid="test-delivery-timeout"
      >
        <XCircle className="h-3.5 w-3.5 text-amber-600" />
        <span className="text-amber-700 dark:text-amber-400 font-medium">
          {retriesExhausted
            ? "Could not confirm delivery — check the Deliveries page for status"
            : "Timed out waiting for delivery result"}
        </span>
        {!retriesExhausted && (
          <button
            onClick={onRetry}
            className="ml-1 inline-flex items-center gap-1 text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 font-medium"
            data-testid="test-delivery-retry"
          >
            <RefreshCw className="h-3 w-3" />
            Retry
          </button>
        )}
        <button
          onClick={onDismiss}
          className="ml-1 text-muted-foreground hover:text-foreground"
          aria-label="Dismiss"
        >
          ✕
        </button>
      </div>
    );
  }

  return (
    <div
      className="flex items-center gap-2 text-xs animate-in fade-in slide-in-from-top-1 duration-200"
      data-testid="test-delivery-failure"
    >
      <XCircle className="h-3.5 w-3.5 text-red-600" />
      <span className="text-red-700 dark:text-red-400 font-medium">
        Failed
        {result.statusCode != null && (
          <> &middot; HTTP {result.statusCode}</>
        )}
        {result.error && (
          <> &middot; {result.error}</>
        )}
      </span>
      {!retriesExhausted ? (
        <button
          onClick={onRetry}
          className="ml-1 inline-flex items-center gap-1 text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 font-medium"
          data-testid="test-delivery-retry"
        >
          <RefreshCw className="h-3 w-3" />
          Retry
        </button>
      ) : (
        <span className="ml-1 text-muted-foreground">
          Check the <Link href={`/app/webhooks/${result.endpointId}`} className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 underline">Deliveries page</Link> for details
        </span>
      )}
      <button
        onClick={onDismiss}
        className="ml-1 text-muted-foreground hover:text-foreground"
        aria-label="Dismiss"
      >
        ✕
      </button>
    </div>
  );
}

function CreateEndpointDialog({
  open,
  onOpenChange,
  onCreated,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated: (secret: string) => void;
}) {
  const [url, setUrl] = useState("");
  const [selected, setSelected] = useState<Set<WebhookEventType>>(
    () => new Set(ALL_EVENT_TYPES),
  );

  const createMutation = useCreateWebhookEndpoint({
    mutation: {
      onSuccess: (data) => {
        toast.success("Webhook endpoint created");
        setUrl("");
        setSelected(new Set(ALL_EVENT_TYPES));
        onOpenChange(false);
        onCreated(data.secret);
      },
      onError: (err: Error) => toast.error(err.message),
    },
  });

  function toggle(type: WebhookEventType) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(type)) next.delete(type);
      else next.add(type);
      return next;
    });
  }

  function submit() {
    if (!url) {
      toast.error("URL is required");
      return;
    }
    if (selected.size === 0) {
      toast.error("Select at least one event type");
      return;
    }
    createMutation.mutate({
      data: { url, eventTypes: Array.from(selected) },
    });
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>New webhook endpoint</DialogTitle>
          <DialogDescription>
            We will POST signed JSON payloads to your URL whenever the selected
            events occur. The signing secret is shown exactly once after
            creation.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="space-y-1.5">
            <Label htmlFor="webhook-url">Endpoint URL</Label>
            <Input
              id="webhook-url"
              type="url"
              placeholder="https://example.com/webhooks/mailcraft"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label>Subscribed event types</Label>
            <div className="grid grid-cols-2 gap-2">
              {ALL_EVENT_TYPES.map((t) => (
                <label
                  key={t}
                  className="flex items-center gap-2 text-sm font-mono cursor-pointer"
                >
                  <Checkbox
                    checked={selected.has(t)}
                    onCheckedChange={() => toggle(t)}
                  />
                  {t}
                </label>
              ))}
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={createMutation.isPending}>
            {createMutation.isPending && (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            )}
            Create
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function RevealSecretDialog({
  secret,
  onClose,
}: {
  secret: string | null;
  onClose: () => void;
}) {
  return (
    <Dialog open={secret !== null} onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Save your signing secret</DialogTitle>
          <DialogDescription>
            This secret is shown <strong>exactly once</strong>. Store it
            somewhere safe — you will not be able to retrieve it again. Use it
            to verify the <code className="font-mono">X-MailCraft-Signature</code>{" "}
            header on each delivery.
          </DialogDescription>
        </DialogHeader>
        <div className="rounded-md border bg-muted/40 p-3 font-mono text-sm break-all">
          {secret}
        </div>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => {
              if (secret) {
                void navigator.clipboard.writeText(secret);
                toast.success("Secret copied to clipboard");
              }
            }}
          >
            <Copy className="mr-2 h-4 w-4" /> Copy
          </Button>
          <Button onClick={onClose}>I have saved it</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
